import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '~/services/api'

interface AuthCheck {
  authenticated: boolean
  user?: { email: string }
}

let _ensurePromise: Promise<void> | null = null
let _epoch = 0 // ponytail: incremented on invalidate() so stale in-flight promises noop

export const useSessionStore = defineStore('session', () => {
  const authenticated = ref<boolean | null>(null)
  const email = ref('')
  const loading = ref(false)

  async function ensure(): Promise<void> {
    if (authenticated.value !== null) return
    if (_ensurePromise) return _ensurePromise

    const myEpoch = ++_epoch
    loading.value = true

    _ensurePromise = api<AuthCheck>('/api/auth/check')
      .then((res) => {
        if (myEpoch !== _epoch) return // ponytail: stale — invalidate() happened while in-flight
        authenticated.value = !!res.authenticated
        email.value = res.user?.email || ''
      })
      .catch(() => {
        if (myEpoch !== _epoch) return
        authenticated.value = false
        email.value = ''
      })
      .finally(() => {
        if (myEpoch !== _epoch) return
        loading.value = false
        _ensurePromise = null
      })

    return _ensurePromise
  }

  function invalidate(): void {
    _epoch++
    authenticated.value = null
    email.value = ''
    loading.value = false
    _ensurePromise = null
  }

  function setEmail(value: string): void {
    email.value = value
  }

  return {
    authenticated,
    email,
    loading,
    ensure,
    invalidate,
    setEmail,
  }
})
